import { useAsync } from "@react-hookz/web";
import differenceInDays from "date-fns/differenceInDays";
import { useAtomValue } from "jotai";
import first from "lodash/first";
import { useEffect, useRef } from "react";
import { fetchQuery, graphql, useFragment, useRelayEnvironment } from "react-relay";
import type { Disposable } from "relay-runtime";
import { createOperationDescriptor, getRequest } from "relay-runtime";
import { removeAtMedia } from "scmp-sdk";

import { data, paywall } from "shared/data";
import { acknowledgementGateArticleIds, article as articleData } from "shared/data/article";
import { section as sectionData } from "shared/data/section";
import { accountAtom } from "shared/lib/account";
import { theme } from "shared/lib/styles";
import { notEmpty, sortArrayByReferenceOrder } from "shared/lib/utils";

import {
  BaseExcludedSectionsInAIEngine,
  ExcludedArticleEntityIdsInAIEngine,
  ExcludedArticleTypesInAIEngine,
  MagazinesStyleSectionsInAIEngine,
} from "scmp-app/components/article/article-list/hooks/const";
import { checkIsStyleArticle } from "scmp-app/components/article/article-render/style-article/helper";
import { fetchAiEngineDataV2 } from "scmp-app/lib/ai-engine/apis";
import { AiEngineDataType } from "scmp-app/lib/ai-engine/enums";
import type { articleRecommendationContent$key } from "scmp-app/queries/__generated__/articleRecommendationContent.graphql";
import type { articleRecommendationQuery } from "scmp-app/queries/__generated__/articleRecommendationQuery.graphql";
import type { helpersCheckIsStyleArticle$key } from "scmp-app/queries/__generated__/helpersCheckIsStyleArticle.graphql";

import type { ArticleRecommendation, PartialContent } from "./types";

export const useArticleRecommendation = (article_: articleRecommendationContent$key) => {
  const content = useFragment(
    graphql`
      fragment articleRecommendationContent on Content {
        entityId
        ... on Article {
          moreOnThisArticles {
            entityId
            publishedDate
            urlAlias
            paywallTypes {
              entityId
            }
            sectionsV2 {
              value {
                ... on Section {
                  entityId
                }
              }
            }
            types {
              value {
                entityId
              }
            }
            urlRedirect {
              toUrl
            }
            ...helpersCheckIsStyleArticle
          }
        }
        ... on Video {
          sectionsV2 {
            value {
              ... on VideoSection {
                entityId
              }
            }
          }
        }
        ...helpersCheckIsStyleArticle
      }
    `,
    article_,
  );

  const environment = useRelayEnvironment();
  const { user } = useAtomValue(accountAtom);
  const isCurrentMagazinesStyleArticle = checkIsStyleArticle(content);

  const includedSections = isCurrentMagazinesStyleArticle ? MagazinesStyleSectionsInAIEngine : [];
  const excludedSections = [
    ...BaseExcludedSectionsInAIEngine,
    ...(isCurrentMagazinesStyleArticle ? [] : MagazinesStyleSectionsInAIEngine),
  ];

  const disposableReference = useRef<Disposable | null>(null);

  const [{ result: articleRecommendation }, { execute: executeFetchRecommendedArticles }] =
    useAsync<ArticleRecommendation>(
      async () => {
        const aiEngineData = await fetchAiEngineDataV2({
          entityId: content.entityId,
          excludedArticleTypes: ExcludedArticleTypesInAIEngine.map(element => element.name),
          excludedSections,
          excludeIds: ExcludedArticleEntityIdsInAIEngine,
          includedSections: includedSections.length > 0 ? includedSections : undefined,
          limit: 20,
          type: AiEngineDataType.MoreOnThisTopic,
          userUuid: user?.uuid,
        });

        const query = graphql`
          query articleRecommendationQuery($entityIds: [String], $first: Int!) {
            contents(filter: { entityIds: $entityIds }, first: $first) {
              edges {
                node {
                  entityId
                  sectionsV2 {
                    value {
                      ... on Section {
                        entityId
                      }
                    }
                  }
                  urlRedirect {
                    toUrl
                  }
                  ... on Article {
                    paywallTypes {
                      entityId
                    }
                    types {
                      value {
                        entityId
                      }
                    }
                  }
                  ...helpersCheckIsStyleArticle
                }
              }
            }
          }
        `;
        const variables = {
          entityIds: aiEngineData.data.results.map(result => result.id) ?? [],
          first: aiEngineData.data.results.length ?? 0,
        };

        const queryRequest = getRequest(query);
        const queryDescriptor = createOperationDescriptor(queryRequest, variables);

        const recommendedArticles = await fetchQuery<articleRecommendationQuery>(
          environment,
          query,
          variables,
        ).toPromise();

        const disposable = environment.retain(queryDescriptor);
        disposableReference.current = disposable;

        const orderedRecommendedArticles = sortArrayByReferenceOrder({
          data: {
            array: recommendedArticles?.contents.edges ?? [],
            idGetter: item => item.node.entityId,
          },
          order: { array: aiEngineData.data.results, idGetter: item => item.id },
        });

        const firstFilteredMoreOnThisArticle = content.moreOnThisArticles?.find(content => {
          if (!notEmpty(content)) return false;

          const isValidUrl = () => !content.urlAlias.match("http")?.length;

          const isExcludedPublishedDate = () => {
            if (!content.publishedDate) return false;
            return differenceInDays(new Date(), new Date(content.publishedDate)) > 30;
          };

          const isExplainerType = () => {
            const mainArticleType = first(content.types);
            if (!mainArticleType?.value) return false;
            return articleData.types.explainer.entityId === first(mainArticleType.value)?.entityId;
          };

          return (
            isValidUrl() &&
            !hasRedirectSetupToExternalDomain(content) &&
            !isAcknowledgementRequiredArticle(content) &&
            !isScmpPlusPaywallArticle(content) &&
            (!isExcludedPublishedDate() || isExplainerType()) &&
            !isExcludedSection(content) &&
            isV2SupportedArticle(content) &&
            passMagazinesStyleArticleTypeChecker(content)
          );
        });

        const isMobileOnly = window.matchMedia(
          removeAtMedia(theme.breakpoints.only("mobile")),
        ).matches;

        const results = orderedRecommendedArticles.reduce<ArticleRecommendation["results"]>(
          (sortedArticleIds, { node: content }) => {
            /*
              Note: Add check of isV2SupportedArticle here to ensure no non-migrated
               articles get included in the list
             */
            const canAddArticle =
              !(
                sortedArticleIds.infiniteScrollArticleIds.includes(content.entityId) ||
                sortedArticleIds.moreOnThisTopicWidgetIds.includes(content.entityId)
              ) && isV2SupportedArticle(content);

            const isWithinInfinityScrollCapacity =
              sortedArticleIds.infiniteScrollArticleIds.length < (isMobileOnly ? 5 : 2);

            const canAddToInfinityScroll =
              isWithinInfinityScrollCapacity &&
              !isAcknowledgementRequiredArticle(content) &&
              !isExcludedSection(content) &&
              !hasRedirectSetupToExternalDomain(content) &&
              !isScmpPlusPaywallArticle(content) &&
              passMagazinesStyleArticleTypeChecker(content);

            if (canAddToInfinityScroll && canAddArticle) {
              sortedArticleIds.infiniteScrollArticleIds.push(content.entityId);
            } else if (canAddArticle) {
              sortedArticleIds.moreOnThisTopicWidgetIds.push(content.entityId);
            }

            if (sortedArticleIds?.infiniteScrollArticleMap) {
              sortedArticleIds.infiniteScrollArticleMap[content.entityId] = content;
            }
            return sortedArticleIds;
          },
          {
            infiniteScrollArticleIds: firstFilteredMoreOnThisArticle
              ? [firstFilteredMoreOnThisArticle.entityId]
              : [],
            infiniteScrollArticleMap: firstFilteredMoreOnThisArticle
              ? {
                  [firstFilteredMoreOnThisArticle.entityId]: firstFilteredMoreOnThisArticle,
                }
              : {},
            moreOnThisTopicWidgetIds: [],
          },
        );

        return {
          allArticleIds: [
            content.entityId,
            ...results.infiniteScrollArticleIds,
            ...results.moreOnThisTopicWidgetIds,
          ],
          firstFilteredMoreOnThisArticleEntityId: firstFilteredMoreOnThisArticle?.entityId,
          recommendationId: aiEngineData.data.rec_id,
          results,
        };

        function passMagazinesStyleArticleTypeChecker(article: helpersCheckIsStyleArticle$key) {
          // Infinite scrolling can have the same magazine style articles
          if (!isCurrentMagazinesStyleArticle) return true;
          return checkIsStyleArticle(article);
        }

        function hasRedirectSetupToExternalDomain(content: PartialContent) {
          if (!content?.urlRedirect?.toUrl) return false;
          try {
            return new URL(content.urlRedirect.toUrl).host !== data.scmpDomains.SCMP;
          } catch {
            // handle the toUrl is relative url without proper host
            return false;
          }
        }

        function isExcludedSection(content: PartialContent) {
          const mainSection = first(content?.sectionsV2);
          if (!mainSection) return false;

          const excludedSectionEntityIds = new Set<string>([
            sectionData.photo.entityId,
            sectionData.sport.racing.entityId,
            sectionData.video.entityId,
          ]);

          return mainSection.value?.some(
            value => value?.entityId && excludedSectionEntityIds.has(value.entityId),
          );
        }

        function isV2SupportedArticle(content: PartialContent) {
          const isExcludedArticleType = () => {
            const mainArticleTypes = first(content?.types);
            if (!mainArticleTypes?.value) return false;

            const excludedArticleTypeEntityIds = new Set<string>(
              ExcludedArticleTypesInAIEngine.map(element => element.entityId),
            );

            const firstArticleTypeValue = first(mainArticleTypes.value)?.entityId ?? "";
            const secondArticleTypeValue =
              mainArticleTypes.value.length > 1 ? (mainArticleTypes.value[1]?.entityId ?? "") : "";

            return (
              excludedArticleTypeEntityIds.has(firstArticleTypeValue) ||
              excludedArticleTypeEntityIds.has(secondArticleTypeValue)
            );
          };

          const isStarlingArticle = () =>
            ExcludedArticleEntityIdsInAIEngine.includes(content?.entityId ?? "");

          return !isExcludedArticleType() && !isStarlingArticle();
        }

        function isAcknowledgementRequiredArticle(content: NonNullable<PartialContent>) {
          return acknowledgementGateArticleIds.has(content.entityId);
        }

        function isScmpPlusPaywallArticle(content: PartialContent) {
          return (content?.paywallTypes ?? [])
            .map(paywallType => paywallType?.entityId ?? "")
            .includes(paywall.scmpPlus.entityId);
        }
      },
      {
        allArticleIds: [],
        firstFilteredMoreOnThisArticleEntityId: "",
        recommendationId: "",
        results: {
          infiniteScrollArticleIds: [],
          moreOnThisTopicWidgetIds: [],
        },
      },
    );

  useEffect(() => {
    void executeFetchRecommendedArticles();
    return () => {
      disposableReference.current?.dispose();
    };
  }, [executeFetchRecommendedArticles]);

  return { articleRecommendation };
};
